import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useMemo, useState } from "react";

import {
  Divider,
  PaymentSummaryContainer,
  PaymentSummaryLabel,
  PaymentSummaryText,
  SummaryInfo,
  SummaryInfoContainer,
} from "../styles";
import {
  CancelCheckoutButton,
  CheckoutButton,
  CheckoutItemsContainer,
} from "../../styles";
import ProductInfo from "../../components/ProductInfo";
import { useFormContext } from "./PaymentMethods/form-context";
import { formatCurrency } from "./PaymentMethods/utils";
import CancelOrderModal from "./CancelOrderModal";

export default function PaymentSummary() {
  const intl = useIntl();
  const {
    selectedPaymentMethod,
    setSelectedPaymentMethodError,
    submitActiveForm,
    submitting,
    cancelOrder,
    cancelling,
  } = useFormContext();
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const { cartProducts, checkoutInfo, currency } = useSelector(
    (state) => state.staff_access
  );

  const totals = useMemo(() => {
    const length = cartProducts.reduce((acc, curr) => acc + curr.qty, 0);
    const subtotal = cartProducts.reduce(
      (acc, curr) => acc + curr.value * curr.qty,
      0
    );
    const totalTaxes = (checkoutInfo?.taxes ?? []).reduce(
      (acc, curr) => acc + curr.value,
      0
    );

    return {
      subtotal: formatCurrency({
        value: subtotal,
        locale: intl.locale,
        currency,
      }),
      length,
      total: subtotal + totalTaxes,
    };
  }, [cartProducts, checkoutInfo, currency, intl.locale]);

  const handleSubmit = async () => {
    if (!selectedPaymentMethod) setSelectedPaymentMethodError(true);
    await submitActiveForm();
  };

  if (!checkoutInfo) return null;

  return (
    <PaymentSummaryContainer>
      <PaymentSummaryText type="bold" mb="24px">
        <FormattedMessage
          id="purchase_summary"
          values={{ number: checkoutInfo.orderNumber }}
        />
      </PaymentSummaryText>

      <CheckoutItemsContainer>
        {cartProducts.map((p) => (
          <ProductInfo
            confirm
            desc={formatCurrency({
              value: p.value,
              locale: intl.locale,
              currency: p.currency,
            })}
            name={p.title[intl.locale]}
            src={null}
            qtd={p.qty}
          />
        ))}
      </CheckoutItemsContainer>

      <Divider />
      <div>
        <PaymentSummaryLabel>
          <FormattedMessage id="checkout_taxes" />
        </PaymentSummaryLabel>

        <SummaryInfoContainer>
          {checkoutInfo.taxes.map((t, i) => (
            <SummaryInfo key={i}>
              <PaymentSummaryText type="text">{t.label}</PaymentSummaryText>
              <PaymentSummaryText type="value">
                {formatCurrency({
                  value: t.value,
                  locale: intl.locale,
                  currency: "BRL",
                })}
              </PaymentSummaryText>
            </SummaryInfo>
          ))}
        </SummaryInfoContainer>
      </div>
      <Divider />

      <SummaryInfoContainer mb="44px">
        <Subtotal
          checkoutInfo={checkoutInfo}
          currency={currency}
          totals={totals}
        />
      </SummaryInfoContainer>

      <CheckoutButton onClick={handleSubmit} loading={submitting}>
        <FormattedMessage id="checkout" />
      </CheckoutButton>

      <CancelCheckoutButton
        onClick={() => setCancelModalOpen(true)}
        disabled={submitting || cancelling}
      >
        <FormattedMessage id="cancel_purchase" />
      </CancelCheckoutButton>

      <CancelOrderModal
        open={cancelModalOpen}
        orderNumber={checkoutInfo.orderNumber}
        loading={cancelling}
        onClose={() => setCancelModalOpen(false)}
        onConfirm={cancelOrder}
      />
    </PaymentSummaryContainer>
  );
}

export function Subtotal({ totals, checkoutInfo, currency }) {
  const intl = useIntl();
  const totalTaxes = checkoutInfo.taxes.reduce(
    (acc, curr) => acc + curr.value,
    0
  );
  return (
    <>
      <SummaryInfo>
        <PaymentSummaryText type="text">
          Subtotal ({totals.length}{" "}
          {intl.formatMessage({ id: totals.length > 1 ? "items" : "item" })})
        </PaymentSummaryText>
        <PaymentSummaryText type="value">{totals.subtotal}</PaymentSummaryText>
      </SummaryInfo>

      <SummaryInfo>
        <PaymentSummaryText type="text">
          <FormattedMessage id="checkout_taxes" />
        </PaymentSummaryText>
        <PaymentSummaryText type="value">
          {formatCurrency({
            value: totalTaxes,
            locale: intl.locale,
            currency: "BRL",
          })}
        </PaymentSummaryText>
      </SummaryInfo>

      <SummaryInfo>
        <PaymentSummaryText type="text">
          <FormattedMessage id="discounts" />
        </PaymentSummaryText>
        <PaymentSummaryText type="value">
          {formatCurrency({
            value: checkoutInfo.discounts > 0 ? checkoutInfo.discounts * -1 : 0,
            locale: intl.locale,
            currency,
          })}
        </PaymentSummaryText>
      </SummaryInfo>

      <SummaryInfo>
        <PaymentSummaryText type="bold">
          <FormattedMessage id="total_value" />
        </PaymentSummaryText>
        <PaymentSummaryText type="bold">
          {formatCurrency({
            value: checkoutInfo.totalValue,
            locale: intl.locale,
            currency,
          })}
        </PaymentSummaryText>
      </SummaryInfo>
    </>
  );
}
